import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Button from 'components/ds/Button'
import Icon from 'components/ds/Icon'
import Skeleton from 'components/ds/Skeleton'
import { Modal, Banner } from 'components/ds/Feedback'
import {
  createAttachmentExport,
  downloadAttachmentExportPart,
  downloadAttachmentsNow,
  estimateAttachmentExport,
  getAttachmentExport,
  getLatestAttachmentExport,
  readErrorMessage,
} from 'actions/attachmentExports'
import styles from './AttachmentExportModal.module.scss'

const POLL_MS = 3000
const RUNNING = ['QUEUED', 'RUNNING']
const EXPLAIN = 'Every attachment of the applications in your current list, in one folder per application.'

const formatBytes = (bytes) => {
  if (bytes == null) return ''
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const count = (n) => (n || 0).toLocaleString()
const plural = (n, word) => `${count(n)} ${word}${n === 1 ? '' : 's'}`
const longDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
const shortDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
const dateTime = (iso) => new Date(iso).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short',
})

/**
 * Export every attachment of the applications the list shows as ZIP files, one folder per application.
 * Opens on an estimate; small exports can download right away, any size can be built in the background
 * (the requester is emailed). Opened with {@code exportId} it shows that background export, e.g. from the
 * email or bell link. Layout and copy follow the Designer "Export attachments" modal.
 */
const AttachmentExportModal = ({ open, onClose, filters, demoMode, exportId }) => {
  const [estimate, setEstimate] = useState(null)
  const [estimateError, setEstimateError] = useState(null)
  const [job, setJob] = useState(null)
  const [jobError, setJobError] = useState(null)
  const [busy, setBusy] = useState(null) // 'job' | 'direct'
  const [directDone, setDirectDone] = useState(false)
  const [latest, setLatest] = useState(null)

  const selection = { filters }

  // Each opening (or change of exportId) is a session. Responses that arrive after their session ended, e.g.
  // a slow lookup for an export the user has navigated away from, are dropped instead of overwriting state.
  const session = useRef(0)
  const current = (id) => session.current === id

  const loadEstimate = useCallback((id) => {
    setEstimate(null)
    setEstimateError(null)
    estimateAttachmentExport({ filters }, demoMode)
      .then(result => current(id) && setEstimate(result))
      .catch(async e => {
        const message = await readErrorMessage(e, "The export couldn't be estimated.")
        if (current(id)) setEstimateError(message)
      })
  }, [filters, demoMode])

  // On open: follow the given export, or show the user's last export plus an estimate of the current list.
  useEffect(() => {
    if (!open) return undefined
    const id = ++session.current
    setJobError(null)
    setDirectDone(false)
    if (exportId) {
      setEstimate(null)
      setJob(null)
      getAttachmentExport(exportId)
        .then(found => current(id) && setJob(found))
        .catch(async e => {
          const message = await readErrorMessage(e, "This export couldn't be found.")
          if (current(id)) setJobError(message)
        })
    } else {
      setJob(existing => (existing && RUNNING.includes(existing.status) ? existing : null))
      setLatest(null)
      getLatestAttachmentExport()
        .then(found => {
          if (!found || !current(id)) return
          if (RUNNING.includes(found.status)) setJob(found)
          else setLatest(found)
        })
        .catch(() => {})
      loadEstimate(id)
    }
    // Ending the session on close/change is the point: the counter isn't a DOM ref, so reading it late is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { session.current++ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, exportId])

  // Follow a background export until it finishes. A poll answer only applies to the export still on screen.
  useEffect(() => {
    if (!job || !RUNNING.includes(job.status)) return undefined
    let active = true
    const timer = setTimeout(() => {
      getAttachmentExport(job.id)
        .then(next => active && setJob(shown => (shown && shown.id === next.id ? next : shown)))
        .catch(() => active && setJob(shown => (shown ? { ...shown } : shown)))
    }, POLL_MS)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [job])

  const startJob = async () => {
    const id = session.current
    setBusy('job')
    try {
      const created = await createAttachmentExport(selection, demoMode)
      if (current(id)) setJob(created)
    } finally {
      setBusy(null)
    }
  }

  const downloadNow = async () => {
    setBusy('direct')
    setEstimateError(null)
    try {
      await downloadAttachmentsNow(selection, demoMode)
      setDirectDone(true)
    } catch (e) {
      setEstimateError(e.message)
    } finally {
      setBusy(null)
    }
  }

  const startOver = () => {
    setJob(null)
    setDirectDone(false)
    loadEstimate(session.current)
  }

  const close = useCallback(() => {
    if (busy !== 'direct') onClose()
  }, [busy, onClose])

  const view = jobError ? 'missing'
    : job ? (RUNNING.includes(job.status) ? 'building' : job.status.toLowerCase())
      : directDone ? 'started'
        : busy === 'direct' ? 'downloading'
          : !estimate && !estimateError ? 'loading'
            : estimate && estimate.tooLarge ? 'toobig'
              : estimate && estimate.files === 0 ? 'empty'
                : 'estimate'

  const reasonId = 'attachment-export-direct-reason'
  const directAllowed = !!estimate?.directDownloadAllowed

  const footerButtons = {
    loading: [
      <Button key="c" variant="ghost" onClick={close}>Cancel</Button>,
      <Button key="d" variant="secondary" disabled startIcon={<Icon name="download" size={16} />}>Download now</Button>,
      <Button key="e" variant="primary" disabled>Email me when it's ready</Button>,
    ],
    estimate: [
      <Button key="c" variant="ghost" onClick={close}>Cancel</Button>,
      <Button
        key="d"
        variant="secondary"
        startIcon={<Icon name="download" size={16} />}
        disabled={!directAllowed || !!busy}
        aria-describedby={directAllowed ? undefined : reasonId}
        onClick={downloadNow}
      >
        Download now
      </Button>,
      <Button key="e" variant="primary" loading={busy === 'job'} disabled={!!busy || !estimate} onClick={startJob}>
        Email me when it's ready
      </Button>,
    ],
    downloading: [
      <Button key="c" variant="ghost" disabled>Cancel</Button>,
      <Button key="d" variant="secondary" loading disabled aria-busy="true">Preparing…</Button>,
      <Button key="e" variant="primary" disabled>Email me when it's ready</Button>,
    ],
    toobig: [
      <Button key="c" variant="ghost" onClick={close}>Close</Button>,
      <Button key="f" variant="primary" onClick={close}>Edit filters</Button>,
    ],
    started: [<Button key="d" variant="primary" onClick={close}>Done</Button>],
    building: [<Button key="c" variant="secondary" onClick={close}>Close</Button>],
    done: [<Button key="d" variant="primary" onClick={close}>Done</Button>],
    failed: [
      <Button key="c" variant="ghost" onClick={close}>Close</Button>,
      <Button key="n" variant="primary" onClick={startOver}>Start new export</Button>,
    ],
    expired: [
      <Button key="c" variant="ghost" onClick={close}>Close</Button>,
      <Button key="n" variant="primary" onClick={startOver}>Start new export</Button>,
    ],
    missing: [<Button key="c" variant="primary" onClick={close}>Close</Button>],
  }
  footerButtons.empty = footerButtons.toobig

  const showSummary = ['estimate', 'downloading', 'toobig', 'empty'].includes(view) && estimate
  const showExplain = ['loading', 'estimate', 'downloading', 'toobig', 'empty'].includes(view)

  return (
    <Modal
      open={open}
      onClose={close}
      title="Export attachments"
      className={styles.modal}
      footer={<div className={styles.footer}>{footerButtons[view]}</div>}
    >
      <div className={styles.body}>
        {latest && view === 'estimate' && (
          <>
            <LastExport job={latest} />
            <div className={styles.divider} />
            <div className={styles.stack4}>
              <span className={styles.eyebrow}>New export</span>
              <span className={styles.caption}>{EXPLAIN}</span>
            </div>
          </>
        )}
        {showExplain && !(latest && view === 'estimate') && <p className={styles.description}>{EXPLAIN}</p>}

        {view === 'loading' && (
          <>
            <div className={styles.summary} aria-busy="true">
              {['60%', '40%', '80%'].map(width => (
                <div key={width} className={styles.stack8}>
                  <Skeleton width={width} height={10} />
                  <Skeleton width="45%" height={18} />
                </div>
              ))}
            </div>
            <span className={styles.caption} role="status">Counting attachments in your current list…</span>
          </>
        )}

        {showSummary && (
          <dl className={styles.summary}>
            <div><dt className={styles.eyebrow}>Applications</dt><dd>{count(estimate.applications)}</dd></div>
            <div><dt className={styles.eyebrow}>Files</dt><dd>{count(estimate.files)}</dd></div>
            <div><dt className={styles.eyebrow}>Estimated size</dt><dd>~{formatBytes(estimate.estimatedBytes)}</dd></div>
          </dl>
        )}

        {['estimate', 'downloading'].includes(view) && estimate?.filesWithUnknownSize > 0 && (
          <InfoNote>
            {plural(estimate.filesWithUnknownSize, 'file')} {estimate.filesWithUnknownSize === 1 ? 'has' : 'have'} no
            recorded size, so the estimate assumes 10 MB each.
          </InfoNote>
        )}

        {view === 'estimate' && estimate && directAllowed && (
          <div className={styles.helper}>
            <span><strong>Download now:</strong> keep this window open until your ZIP is ready.</span>
            <span><strong>Email me when it's ready:</strong> close this window and we'll email you a link.</span>
          </div>
        )}

        {view === 'estimate' && estimate && !directAllowed && (
          <div id={reasonId}>
            <Banner tone="info">
              Too large to download directly (limit {formatBytes(estimate.directDownloadMaxBytes)} and{' '}
              {count(estimate.directDownloadMaxFiles)} files). We'll build it in the background and email you a link.
            </Banner>
          </div>
        )}

        {view === 'toobig' && (
          <Banner tone="watch" title="Too large to export">
            Exports are limited to {formatBytes(estimate.maxBytes)}. Narrow the filters on the Applications list to fewer
            applications, then export again.
          </Banner>
        )}

        {view === 'empty' && (
          <Banner tone="info" title="Nothing to export">
            The applications in your list have no attachments. Change the filters to include other applications.
          </Banner>
        )}

        {view === 'downloading' && <Banner tone="info">Preparing your ZIP… keep this window open.</Banner>}

        {view === 'started' && <Banner tone="success" title="Download started">Check your browser's downloads.</Banner>}

        {estimateError && view === 'estimate' && <Banner tone="error" title="Something went wrong">{estimateError}</Banner>}

        {view === 'building' && <Building job={job} />}

        {view === 'done' && <Ready job={job} />}

        {view === 'failed' && (
          <>
            <Banner tone="error" title="Export failed">
              We couldn't build your ZIP files. Nothing in your applications was changed. Start a new export to try again.
            </Banner>
            <span className={styles.caption}>
              Reference <span className={styles.mono}>{job.id.slice(0, 8).toUpperCase()}</span>
              {job.finishedAt && ` · ${dateTime(job.finishedAt)}`}
            </span>
          </>
        )}

        {view === 'expired' && (
          <Banner tone="info" title="This export has expired">
            Its files were deleted after 7 days. Start a new export to get the attachments again.
          </Banner>
        )}

        {view === 'missing' && <Banner tone="error" title="Export not available">{jobError}</Banner>}
      </div>
    </Modal>
  )
}

const InfoNote = ({ children }) => (
  <div className={styles.note}>
    <Icon name="info" size={16} className={styles.noteIcon} />
    <span className={styles.caption}>{children}</span>
  </div>
)

const PartList = ({ job }) => (
  <div className={styles.parts}>
    {job.parts.map(part => (
      <div key={part.partNo} className={styles.part}>
        <Icon name="file" size={18} className={styles.partIcon} />
        <span className={styles.mono}>{part.fileName}</span>
        <span className={styles.caption}>{formatBytes(part.sizeBytes)}</span>
        <Button
          variant="secondary"
          size="small"
          startIcon={<Icon name="download" size={16} />}
          aria-label={`Download ${part.fileName}`}
          onClick={() => downloadAttachmentExportPart(job.id, part.partNo)}
        >
          Download
        </Button>
      </div>
    ))}
  </div>
)

/** The user's last finished export, still downloadable, shown above the options for a new one. */
const LastExport = ({ job }) => (
  <div className={styles.stack12}>
    <span className={styles.eyebrow}>Your last export</span>
    <div className={styles.lastCard}>
      <span className={styles.lastLine}>
        {shortDate(job.createdAt)} · {plural(job.fileCount, 'file')} · {formatBytes(job.sizeBytes)}
        {job.expiresAt && ` · Available until ${longDate(job.expiresAt)}`}
      </span>
      <PartList job={job} />
    </div>
  </div>
)

const Building = ({ job }) => {
  const percent = job.totalApplications ? Math.round((job.processedApplications / job.totalApplications) * 100) : 0
  return (
    <>
      <div className={styles.stack10}>
        <div className={styles.buildingHead}>
          <span className={styles.buildingTitle}>{job.status === 'QUEUED' ? 'Waiting to start…' : 'Building your ZIP files…'}</span>
          <span className={styles.secondary}>{count(job.processedApplications)} of {plural(job.totalApplications, 'application')}</span>
        </div>
        <div className={styles.bar} role="progressbar" aria-label="Applications packaged" aria-valuemin={0}
          aria-valuemax={job.totalApplications} aria-valuenow={job.processedApplications}>
          <div className={styles.barFill} style={{ width: `${Math.max(percent, 2)}%` }} />
        </div>
        <span className={styles.caption}>
          Started {dateTime(job.createdAt)} · {plural(job.estimatedFiles, 'file')} · ~{formatBytes(job.estimatedBytes)}
        </span>
      </div>
      <Banner tone="info">You can close this window. We'll email you when it's ready.</Banner>
    </>
  )
}

const Ready = ({ job }) => (
  <>
    <Banner tone="success" title="Your export is ready">
      {plural(job.fileCount, 'file')} from {plural(job.totalApplications, 'application')} · {formatBytes(job.sizeBytes)}
    </Banner>
    {job.missingFiles > 0 && (
      <InfoNote>
        {plural(job.missingFiles, 'file')} couldn't be included because {job.missingFiles === 1 ? "it's" : "they're"} no
        longer in storage. Each ZIP has a manifest.csv that lists them.
      </InfoNote>
    )}
    <div className={styles.stack8}>
      <span className={styles.eyebrow}>{job.parts.length > 1 ? `${job.parts.length} ZIP parts` : 'ZIP file'}</span>
      <PartList job={job} />
      {job.expiresAt && <span className={styles.caption}>Available until {longDate(job.expiresAt)}.</span>}
    </div>
  </>
)

AttachmentExportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filters: PropTypes.object,
  demoMode: PropTypes.bool,
  exportId: PropTypes.string,
}

InfoNote.propTypes = { children: PropTypes.node }
PartList.propTypes = { job: PropTypes.object.isRequired }
LastExport.propTypes = { job: PropTypes.object.isRequired }
Building.propTypes = { job: PropTypes.object.isRequired }
Ready.propTypes = { job: PropTypes.object.isRequired }

export default AttachmentExportModal
